import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { VisitTip, VisitTipInsert, VisitTipWithProfile } from '@/types/VisitTip';
import { toast } from '@/hooks/use-toast';

export const useVisitTips = (placeName: string, language: 'FR' | 'EN') => {
  const [tips, setTips] = useState<VisitTipWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchTips = async () => {
    try {
      const { data, error } = await supabase
        .from('visit_tips')
        .select(`
          *,
          profiles:user_id (
            full_name,
            avatar_url
          )
        `)
        .eq('place_name', placeName)
        .eq('language', language)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTips(data as any || []);
    } catch (error) {
      console.error('Error fetching visit tips:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les conseils de visite",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTips();
  }, [placeName, language]);

  const addTip = async (content: string): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour ajouter un conseil",
        variant: "destructive"
      });
      return false;
    }

    if (content.trim().length < 10) {
      toast({
        title: "Conseil trop court",
        description: "Le conseil doit contenir au moins 10 caractères",
        variant: "destructive"
      });
      return false;
    }

    try {
      const newTip: VisitTipInsert = {
        place_name: placeName,
        content: content.trim(),
        language,
        user_id: user.id
      };

      const { error } = await supabase
        .from('visit_tips')
        .insert(newTip);

      if (error) throw error;

      toast({
        title: "Conseil ajouté",
        description: "Votre conseil a été ajouté avec succès"
      });
      
      fetchTips();
      return true;
    } catch (error) {
      console.error('Error adding tip:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le conseil",
        variant: "destructive"
      });
      return false;
    }
  };

  const toggleReaction = async (tipId: string, type: 'like' | 'dislike') => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour réagir",
        variant: "destructive"
      });
      return;
    }

    try {
      const tip = tips.find(t => t.id === tipId);
      if (!tip) return;

      const likesArray = [...tip.likes_user_ids];
      const dislikesArray = [...tip.dislikes_user_ids];
      
      if (type === 'like') {
        const hasLiked = likesArray.includes(user.id);
        const hasDisliked = dislikesArray.includes(user.id);
        
        if (hasLiked) {
          // Remove like
          const index = likesArray.indexOf(user.id);
          likesArray.splice(index, 1);
        } else {
          // Add like and remove dislike if exists
          likesArray.push(user.id);
          if (hasDisliked) {
            const dislikeIndex = dislikesArray.indexOf(user.id);
            dislikesArray.splice(dislikeIndex, 1);
          }
        }
      } else {
        const hasDisliked = dislikesArray.includes(user.id);
        const hasLiked = likesArray.includes(user.id);
        
        if (hasDisliked) {
          // Remove dislike
          const index = dislikesArray.indexOf(user.id);
          dislikesArray.splice(index, 1);
        } else {
          // Add dislike and remove like if exists
          dislikesArray.push(user.id);
          if (hasLiked) {
            const likeIndex = likesArray.indexOf(user.id);
            likesArray.splice(likeIndex, 1);
          }
        }
      }

      const { error } = await supabase
        .from('visit_tips')
        .update({
          likes_user_ids: likesArray,
          dislikes_user_ids: dislikesArray
        })
        .eq('id', tipId);

      if (error) throw error;
      
      fetchTips();
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la réaction",
        variant: "destructive"
      });
    }
  };

  const reportTip = async (tipId: string, reason: string) => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour signaler",
        variant: "destructive"
      });
      return;
    }

    try {
      const tip = tips.find(t => t.id === tipId);
      if (!tip) return;

      const reportsArray = [...tip.reports_user_ids];
      const reasonsArray = [...tip.report_reasons];

      if (reportsArray.includes(user.id)) {
        toast({
          title: "Déjà signalé",
          description: "Vous avez déjà signalé ce conseil",
          variant: "destructive"
        });
        return;
      }

      reportsArray.push(user.id);
      reasonsArray.push(reason);

      // Auto-hide if 3+ reports
      const shouldHide = reportsArray.length >= 3;

      const { error } = await supabase
        .from('visit_tips')
        .update({
          reports_user_ids: reportsArray,
          report_reasons: reasonsArray,
          is_hidden: shouldHide
        })
        .eq('id', tipId);

      if (error) throw error;

      toast({
        title: "Signalement enregistré",
        description: "Le conseil a été signalé, merci pour votre vigilance"
      });

      fetchTips();
    } catch (error) {
      console.error('Error reporting tip:', error);
      toast({
        title: "Erreur",
        description: "Impossible de signaler le conseil",
        variant: "destructive"
      });
    }
  };

  return {
    tips,
    loading,
    addTip,
    toggleReaction,
    reportTip,
    refetch: fetchTips
  };
};